import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Mail,
  RefreshCw,
  Loader2,
  AlertTriangle,
  X,
  PauseCircle,
  Archive,
  PlayCircle,
  Bell,
  Check,
  ArrowUpCircle,
  ArrowDownCircle,
  Link2,
  Copy,
} from 'lucide-react';
import { CreateInstitutionModal } from '../components/CreateInstitutionModal';
import { ViewInstitutionModal } from '../components/ViewInstitutionModal';
import { EditInstitutionModal } from '../components/EditInstitutionModal';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import {
  type SolutionChangeRequest,
  getAllPendingRequests,
  getPendingRequestsCount,
  approveRequest,
  rejectRequest,
  getSolutionLabel,
  getRequestTypeLabel,
} from '@/services/solutionRequestsService';

// Delete Confirmation Modal Component
interface DeleteConfirmModalProps {
  isOpen: boolean;
  institutionName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  institutionName,
  onConfirm,
  onCancel,
  isDeleting,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-gray-100">
          <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">
              Supprimer l'établissement
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Cette action est irréversible
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer <span className="font-semibold text-gray-900">"{institutionName}"</span> ?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Toutes les données associées à cet établissement seront définitivement supprimées, y compris les utilisateurs, les cours et les fichiers.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Supprimer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface Institution {
  id: string;
  name: string;
  slug: string;
  subdomain: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'TRIAL' | 'PENDING_VERIFICATION';
  subscriptionPlan: string | null;
  subscriptionEnds: string | null;
  createdAt: string;
  dgName?: string;
  dgEmail?: string;
  dgEmailVerified?: boolean;
  usersCount?: number;
}

const statusConfig = {
  ACTIVE: {
    label: 'Actif',
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    dotColor: 'bg-green-500',
  },
  INACTIVE: {
    label: 'Inactif',
    icon: XCircle,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    dotColor: 'bg-gray-500',
  },
  SUSPENDED: {
    label: 'Suspendu',
    icon: AlertCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    dotColor: 'bg-red-500',
  },
  TRIAL: {
    label: 'Essai',
    icon: Clock,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    dotColor: 'bg-amber-500',
  },
  PENDING_VERIFICATION: {
    label: 'En attente',
    icon: Mail,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    dotColor: 'bg-blue-500',
  },
};

export const InstitutionsPage: React.FC = () => {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [deletingInstitution, setDeletingInstitution] = useState<string | null>(null);
  const [deleteModalData, setDeleteModalData] = useState<{ id: string; name: string } | null>(null);
  const [viewingInstitutionId, setViewingInstitutionId] = useState<string | null>(null);
  const [editingInstitution, setEditingInstitution] = useState<Institution | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Solution requests state
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState<SolutionChangeRequest[]>([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<{ isOpen: boolean; requestId: string | null }>({ isOpen: false, requestId: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInstitutions = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setInstitutions(data);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch pending requests count
  const fetchPendingRequestsCount = useCallback(async () => {
    try {
      const count = await getPendingRequestsCount();
      setPendingRequestsCount(count);
    } catch (error) {
      console.error('Error fetching pending requests count:', error);
      // Don't crash the page, just set count to 0
      setPendingRequestsCount(0);
    }
  }, []);

  // Fetch all pending requests
  const fetchPendingRequests = async () => {
    setLoadingRequests(true);
    try {
      const requests = await getAllPendingRequests();
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      toast.error('Erreur', 'Impossible de charger les demandes');
      setPendingRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Handle approve request
  const handleApproveRequest = async (requestId: string) => {
    setProcessingRequest(requestId);
    try {
      await approveRequest(requestId);
      toast.success('Approuvee', 'La demande a ete approuvee et les solutions ont ete mises a jour');
      await fetchPendingRequests();
      await fetchPendingRequestsCount();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Erreur', 'Erreur lors de l\'approbation de la demande');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle reject request
  const handleRejectRequest = async () => {
    if (!rejectModal.requestId || !rejectionReason.trim()) return;

    setProcessingRequest(rejectModal.requestId);
    try {
      await rejectRequest(rejectModal.requestId, { reason: rejectionReason.trim() });
      toast.success('Refusee', 'La demande a ete refusee');
      setRejectModal({ isOpen: false, requestId: null });
      setRejectionReason('');
      await fetchPendingRequests();
      await fetchPendingRequestsCount();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Erreur', 'Erreur lors du refus de la demande');
    } finally {
      setProcessingRequest(null);
    }
  };

  // Open requests modal
  const openRequestsModal = async () => {
    setShowRequestsModal(true);
    await fetchPendingRequests();
  };

  useEffect(() => {
    fetchInstitutions();
    fetchPendingRequestsCount();
  }, [fetchPendingRequestsCount]);

  const filteredInstitutions = institutions.filter((institution) => {
    const matchesSearch =
      institution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institution.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institution.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || institution.status === statusFilter;
    // Hide archived (INACTIVE) by default unless showArchived is true or INACTIVE is explicitly selected
    const includeArchived = showArchived || statusFilter === 'INACTIVE' || institution.status !== 'INACTIVE';
    return matchesSearch && matchesStatus && includeArchived;
  });

  // Count of archived institutions
  const archivedCount = institutions.filter((i) => i.status === 'INACTIVE').length;

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    fetchInstitutions();
  };

  const handleResendVerification = async (institutionId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setResendingEmail(institutionId);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}/resend-verification`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success('Email envoyé', 'L\'email de vérification a été renvoyé avec succès');
      } else {
        const data = await response.json();
        toast.error('Erreur', data.message || 'Erreur lors du renvoi de l\'email');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error('Erreur', 'Erreur de connexion au serveur');
    } finally {
      setResendingEmail(null);
    }
    setShowActionsMenu(null);
  };

  const openDeleteModal = (institution: Institution) => {
    setDeleteModalData({ id: institution.id, name: institution.name });
    setShowActionsMenu(null);
  };

  const handleDeleteInstitution = async () => {
    if (!deleteModalData) return;

    setDeletingInstitution(deleteModalData.id);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${deleteModalData.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success('Supprime', 'L\'etablissement a ete supprime avec succes');
        setDeleteModalData(null);
        fetchInstitutions();
      } else {
        const data = await response.json();
        toast.error('Erreur', data.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting institution:', error);
      toast.error('Erreur', 'Erreur de connexion au serveur');
    } finally {
      setDeletingInstitution(null);
    }
  };

  const handleSuspend = async (institutionId: string) => {
    setActionLoading(`suspend-${institutionId}`);
    setShowActionsMenu(null);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}/suspend`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: 'Facture impayee' }),
        }
      );
      if (response.ok) {
        toast.success('Suspendu', 'L\'etablissement a ete suspendu');
        fetchInstitutions();
      } else {
        const data = await response.json();
        toast.error('Erreur', data.message || 'Erreur lors de la suspension');
      }
    } catch (error) {
      console.error('Error suspending institution:', error);
      toast.error('Erreur', 'Erreur de connexion au serveur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (institutionId: string) => {
    setActionLoading(`archive-${institutionId}`);
    setShowActionsMenu(null);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}/archive`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success('Archive', 'L\'etablissement a ete archive');
        fetchInstitutions();
      } else {
        const data = await response.json();
        toast.error('Erreur', data.message || 'Erreur lors de l\'archivage');
      }
    } catch (error) {
      console.error('Error archiving institution:', error);
      toast.error('Erreur', 'Erreur de connexion au serveur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (institutionId: string) => {
    setActionLoading(`reactivate-${institutionId}`);
    setShowActionsMenu(null);
    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}/reactivate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        toast.success('Reactive', 'L\'etablissement a ete reactive');
        fetchInstitutions();
      } else {
        const data = await response.json();
        toast.error('Erreur', data.message || 'Erreur lors de la reactivation');
      }
    } catch (error) {
      console.error('Error reactivating institution:', error);
      toast.error('Erreur', 'Erreur de connexion au serveur');
    } finally {
      setActionLoading(null);
    }
  };

  const StatusBadge = ({ status }: { status: Institution['status'] }) => {
    const config = statusConfig[status] || statusConfig.INACTIVE;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  return (
    <>
      {/* Toast Notifications */}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Gestion des Établissements et Utilisateurs
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les institutions et leurs directeurs généraux
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Plus size={20} />
          Nouvel Établissement
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-xl font-bold text-gray-900">{institutions.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Actifs</p>
              <p className="text-xl font-bold text-gray-900">
                {institutions.filter((i) => i.status === 'ACTIVE').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Mail size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-xl font-bold text-gray-900">
                {institutions.filter((i) => i.status === 'PENDING_VERIFICATION').length}
              </p>
            </div>
          </div>
        </div>
        {/* Pending Solution Requests Card - Highlighted when there are pending requests */}
        <button
          onClick={openRequestsModal}
          className={`text-left rounded-xl border p-4 transition-all hover:shadow-md ${
            pendingRequestsCount > 0
              ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300 ring-2 ring-amber-200'
              : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center relative ${
              pendingRequestsCount > 0 ? 'bg-amber-200' : 'bg-amber-100'
            }`}>
              <Bell size={20} className={pendingRequestsCount > 0 ? 'text-amber-700' : 'text-amber-600'} />
              {pendingRequestsCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center animate-pulse">
                  {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                </span>
              )}
            </div>
            <div>
              <p className={`text-sm ${pendingRequestsCount > 0 ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                Demandes de validation
              </p>
              <p className={`text-xl font-bold ${pendingRequestsCount > 0 ? 'text-amber-800' : 'text-gray-900'}`}>
                {pendingRequestsCount}
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un établissement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="ACTIVE">Actif</option>
              <option value="TRIAL">Essai</option>
              <option value="PENDING_VERIFICATION">En attente</option>
              <option value="SUSPENDED">Suspendu</option>
              <option value="INACTIVE">Inactif</option>
            </select>
          </div>
          {/* Show Archived Toggle */}
          {archivedCount > 0 && (
            <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <Archive size={16} className="text-gray-500" />
              <span className="text-sm text-gray-700">
                Afficher les archives ({archivedCount})
              </span>
            </label>
          )}
        </div>
      </div>

      {/* Institutions Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Établissement
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Directeur Général
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Statut
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Abonnement
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Créé le
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Chargement...
                  </td>
                </tr>
              ) : filteredInstitutions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
                    <p>Aucun établissement trouvé</p>
                    <button
                      onClick={() => setIsCreateModalOpen(true)}
                      className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Créer un nouvel établissement
                    </button>
                  </td>
                </tr>
              ) : (
                filteredInstitutions.map((institution) => (
                  <tr key={institution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Building2 size={20} className="text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{institution.name}</p>
                          {institution.city && (
                            <p className="text-sm text-gray-500">{institution.city}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {institution.dgName ? (
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{institution.dgName}</p>
                            <div className="flex items-center gap-1">
                              <p className="text-sm text-gray-500">{institution.dgEmail}</p>
                              {institution.dgEmailVerified ? (
                                <CheckCircle size={14} className="text-green-500" />
                              ) : (
                                <Clock size={14} className="text-amber-500" />
                              )}
                            </div>
                          </div>
                          {!institution.dgEmailVerified && (
                            <button
                              onClick={(e) => handleResendVerification(institution.id, e)}
                              disabled={resendingEmail === institution.id}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50"
                              title="Renvoyer l'email de vérification"
                            >
                              {resendingEmail === institution.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Renvoyer
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Non assigné</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={institution.status} />
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900 capitalize">
                        {institution.subscriptionPlan || 'Basic'}
                      </p>
                      {institution.subscriptionEnds && (
                        <p className="text-sm text-gray-500">
                          Expire le {new Date(institution.subscriptionEnds).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(institution.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              if (showActionsMenu === institution.id) {
                                setShowActionsMenu(null);
                                setMenuPosition(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                  top: rect.top - 8,
                                  left: rect.right - 220,
                                });
                                setShowActionsMenu(institution.id);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical size={18} />
                          </button>
                          {showActionsMenu === institution.id && menuPosition && createPortal(
                            <>
                              <div
                                className="fixed inset-0 z-[9998]"
                                onClick={() => {
                                  setShowActionsMenu(null);
                                  setMenuPosition(null);
                                }}
                              />
                              <div
                                ref={menuRef}
                                style={{
                                  position: 'fixed',
                                  top: menuPosition.top,
                                  left: menuPosition.left,
                                  transform: 'translateY(-100%)',
                                }}
                                className="w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
                              >
                                {/* View Details */}
                                <button
                                  onClick={() => {
                                    setViewingInstitutionId(institution.id);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Eye size={16} />
                                  Voir les details
                                </button>
                                {/* Copy Login URL */}
                                <button
                                  onClick={() => {
                                    // In development, use localhost with tenant param
                                    // In production, use subdomain-based URL
                                    const isDev = import.meta.env.DEV;
                                    let loginUrl: string;

                                    if (isDev) {
                                      // Development: use localhost with tenant ID
                                      loginUrl = `http://localhost:3000/login?tenant=${institution.id}`;
                                    } else {
                                      // Production: use subdomain
                                      const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'nexedu.ma';
                                      loginUrl = `https://${institution.subdomain}.${baseDomain}/login`;
                                    }

                                    navigator.clipboard.writeText(loginUrl).then(() => {
                                      toast.success('URL copiee', `L'URL de connexion a ete copiee dans le presse-papier`);
                                    }).catch(() => {
                                      toast.error('Erreur', 'Impossible de copier l\'URL');
                                    });
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full flex items-center justify-start gap-3 px-4 py-2.5 text-sm text-left text-gray-700 hover:bg-gray-100"
                                >
                                  <Link2 size={16} className="flex-shrink-0" />
                                  <span>Copier l'URL de connexion</span>
                                </button>
                                {/* Edit */}
                                <button
                                  onClick={() => {
                                    setEditingInstitution(institution);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <Edit size={16} />
                                  Modifier
                                </button>
                                {/* Resend Email if not verified */}
                                {!institution.dgEmailVerified && (
                                  <button
                                    onClick={() => handleResendVerification(institution.id)}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    <Mail size={16} />
                                    Renvoyer l'email
                                  </button>
                                )}
                                <hr className="my-1 border-gray-200" />
                                {/* Suspend/Reactivate based on status */}
                                {institution.status === 'SUSPENDED' || institution.status === 'INACTIVE' ? (
                                  <button
                                    onClick={() => handleReactivate(institution.id)}
                                    disabled={actionLoading === `reactivate-${institution.id}`}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
                                  >
                                    {actionLoading === `reactivate-${institution.id}` ? (
                                      <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                      <PlayCircle size={16} />
                                    )}
                                    Reactiver
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleSuspend(institution.id)}
                                      disabled={actionLoading === `suspend-${institution.id}`}
                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                                    >
                                      {actionLoading === `suspend-${institution.id}` ? (
                                        <Loader2 size={16} className="animate-spin" />
                                      ) : (
                                        <PauseCircle size={16} />
                                      )}
                                      Suspendre (facturation)
                                    </button>
                                    <button
                                      onClick={() => handleArchive(institution.id)}
                                      disabled={actionLoading === `archive-${institution.id}`}
                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                    >
                                      {actionLoading === `archive-${institution.id}` ? (
                                        <Loader2 size={16} className="animate-spin" />
                                      ) : (
                                        <Archive size={16} />
                                      )}
                                      Archiver
                                    </button>
                                  </>
                                )}
                                <hr className="my-1 border-gray-200" />
                                <button
                                  onClick={() => openDeleteModal(institution)}
                                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={16} />
                                  Supprimer
                                </button>
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Institution Modal */}
      <CreateInstitutionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteModalData}
        institutionName={deleteModalData?.name || ''}
        onConfirm={handleDeleteInstitution}
        onCancel={() => setDeleteModalData(null)}
        isDeleting={!!deletingInstitution}
      />

      {/* View Institution Modal */}
      <ViewInstitutionModal
        isOpen={!!viewingInstitutionId}
        onClose={() => setViewingInstitutionId(null)}
        institutionId={viewingInstitutionId}
      />

      {/* Edit Institution Modal */}
      <EditInstitutionModal
        isOpen={!!editingInstitution}
        onClose={() => setEditingInstitution(null)}
        onSuccess={() => {
          setEditingInstitution(null);
          fetchInstitutions();
          toast.success('Modifications enregistrees', 'L\'etablissement a ete mis a jour avec succes');
        }}
        institution={editingInstitution}
      />

      {/* Pending Solution Requests Modal */}
      {showRequestsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowRequestsModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Bell size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Demandes de Validation
                  </h3>
                  <p className="text-sm text-gray-500">
                    {pendingRequests.length} demande{pendingRequests.length !== 1 ? 's' : ''} en attente
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRequestsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              {loadingRequests ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-primary-600" />
                </div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle size={48} className="mx-auto text-green-300 mb-4" />
                  <p className="text-gray-500 font-medium">Aucune demande en attente</p>
                  <p className="text-sm text-gray-400 mt-1">Toutes les demandes ont ete traitees</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div
                      key={request.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-gray-900">
                              {request.tenantName || 'Institution'}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              request.requestType === 'ACTIVATE'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {request.requestType === 'ACTIVATE' ? (
                                <ArrowUpCircle size={12} />
                              ) : (
                                <ArrowDownCircle size={12} />
                              )}
                              {getRequestTypeLabel(request.requestType)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            Demande {request.requestType === 'ACTIVATE' ? "d'activation" : 'de desactivation'} de{' '}
                            <span className="font-medium">{getSolutionLabel(request.solution)}</span>
                          </p>
                          <p className="text-xs text-gray-400">
                            Par {request.requestedByName || 'Utilisateur'} - {new Date(request.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleApproveRequest(request.id)}
                            disabled={processingRequest === request.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            {processingRequest === request.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Check size={14} />
                            )}
                            Approuver
                          </button>
                          <button
                            onClick={() => setRejectModal({ isOpen: true, requestId: request.id })}
                            disabled={processingRequest === request.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            <X size={14} />
                            Refuser
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setShowRequestsModal(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setRejectModal({ isOpen: false, requestId: null });
              setRejectionReason('');
            }}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center gap-4 p-6 border-b border-gray-100">
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Refuser la demande
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Veuillez indiquer le motif du refus
                </p>
              </div>
              <button
                onClick={() => {
                  setRejectModal({ isOpen: false, requestId: null });
                  setRejectionReason('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motif du refus *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Expliquez pourquoi cette demande est refusee..."
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
              <p className="mt-2 text-xs text-gray-500">
                Ce motif sera communique au demandeur par notification.
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => {
                  setRejectModal({ isOpen: false, requestId: null });
                  setRejectionReason('');
                }}
                disabled={!!processingRequest}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleRejectRequest}
                disabled={!rejectionReason.trim() || !!processingRequest}
                className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {processingRequest ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <X size={16} />
                    Confirmer le refus
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};
